import { useDispatch, useSelector } from 'react-redux';
import { Toast, useToast } from '@zillow/constellation';
import type { LogData } from '@zg-rentals/id-verification';
import { ExitReason, IdVerification, LogLevel, TermSet } from '@zg-rentals/id-verification';
import {
  idVerifyingCompletedLinkSelector,
  idVerifyingPreviousLinkSelector,
  userIdIreneSelector,
  zgBaseUrlSelector,
} from '../../selectors';
import { useHistory } from 'react-router-dom';
import { getApplicationsTabPath } from '../../constants/urls';
import useQueryVariables from '../../hooks/useQueryVariables';
import { fetchIdVerificationStatusSuccess, fetchTermsStatusSuccess } from '../../actions/landlordActionCreators';
import { termsStatusTypes } from '../../constants/statusConstants';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackListingUserEvent } from '../../../client/analytics';
import { useListingUser } from '../../hooks/useListingUser';

const logger = getGlobalLogger(__filename)!;

export default function IdVerificationModal() {
  const history = useHistory();
  const zgBaseUrl = useSelector(zgBaseUrlSelector);
  const { propertyId } = useQueryVariables();
  const userId = useSelector(userIdIreneSelector);
  const idVerifyingCompletedLink = useSelector(idVerifyingCompletedLinkSelector);
  const idVerifyingPreviousLink = useSelector(idVerifyingPreviousLinkSelector);
  const { listing, user } = useListingUser();
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();

  function onNavigateToPrevious() {
    history.replace(idVerifyingPreviousLink ? idVerifyingPreviousLink : getApplicationsTabPath(propertyId));
  }

  function onIdVerified() {
    dispatch(fetchIdVerificationStatusSuccess({ isIdVerified: true }));
    dispatch(fetchTermsStatusSuccess({ termsStatus: termsStatusTypes.TAC_CURRENTLY_ACCEPTED }));
    history.replace(idVerifyingCompletedLink ? idVerifyingCompletedLink : getApplicationsTabPath(propertyId));
  }

  function onExit({ success, reason }: { success: boolean; reason: ExitReason }) {
    if (success && reason === ExitReason.IdVerified) {
      enqueueToast(<Toast appearance="success" body="Identity verified" />);
      onIdVerified();
    } else {
      onNavigateToPrevious();
    }
  }

  function log(logLevel: LogLevel, logData: LogData) {
    const { message, error } = logData;
    switch (logLevel) {
      case LogLevel.Error:
        logger.error({ userId }, `${message} --- %o`, error);
        break;
      case LogLevel.Warn:
        logger.warn({ userId }, `${message} --- %o`, error);
        break;
      case LogLevel.Info:
        logger.info({ userId }, `${message} --- %o`, error);
        break;
      default:
        logger.warn({ userId }, `${message} --- %o`, error);
        break;
    }
  }

  return (
    <IdVerification
      id="ra-landlord-web-ssr"
      onExit={onExit}
      termSet={TermSet.LandlordScreening}
      zgApi={zgBaseUrl}
      trackEvent={(basePayload) => {
        trackListingUserEvent({
          ...basePayload,
          ...getZrmUserInfo(user),
          ...getZrmListingInfo(listing),
        });
      }}
      log={log}
    />
  );
}
